/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Global caching system for better performance across the entire app
 */

// Global cache with different TTLs for different data types
const globalCache = new Map<string, {
  data: any
  timestamp: number
  ttl: number
}>()

// Cache TTLs in milliseconds
export const CACHE_TTLS = {
  PRODUCTS: 5 * 60 * 1000,      // 5 minutes
  SITE_SETTINGS: 10 * 60 * 1000, // 10 minutes
  CATEGORIES: 15 * 60 * 1000,    // 15 minutes
  TRANSLATIONS: 30 * 60 * 1000,  // 30 minutes
  IMAGES: 60 * 60 * 1000,        // 1 hour
  USER_DATA: 2 * 60 * 1000       // 2 minutes
} as const

/**
 * Set cache entry with TTL
 */
export function setCache(key: string, data: any, ttl: number): void {
  globalCache.set(key, {
    data,
    timestamp: Date.now(),
    ttl
  })
}

/**
 * Get cache entry if valid
 */
export function getCache<T = any>(key: string): T | null {
  const entry = globalCache.get(key)
  
  if (!entry) return null
  
  const now = Date.now()
  if (now - entry.timestamp > entry.ttl) {
    globalCache.delete(key)
    return null
  }
  
  return entry.data as T
}

/**
 * Clear cache by key or pattern
 */
export function clearCache(keyOrPattern: string): void {
  if (keyOrPattern.includes('*')) {
    const pattern = keyOrPattern.replace('*', '')
    for (const key of globalCache.keys()) {
      if (key.startsWith(pattern)) {
        globalCache.delete(key)
      }
    }
  } else {
    globalCache.delete(keyOrPattern)
  }
}

/**
 * Get cache stats for monitoring
 */
export function getCacheStats(): {
  size: number
  keys: string[]
  hitRate?: number
} {
  return {
    size: globalCache.size,
    keys: Array.from(globalCache.keys())
  }
}

/**
 * Preload critical data into cache
 */
export async function preloadCriticalData(locale: string): Promise<void> {
  try {
    // Preload in parallel using client-side functions
    await Promise.allSettled([
      // Preload site settings
      import('@/lib/optimized-site-settings-client').then(m => m.getSiteSettingsClient()),
      // Preload first batch of products
      import('@/lib/optimized-products-client').then(m => m.getOptimizedProductsClient(locale, 20))
    ])
  } catch (error) {
    console.warn('Failed to preload critical data:', error)
  }
}